import React, { useMemo } from 'react';
import { Pressable, StyleSheet } from 'react-native';
import Collapsible from "../../util/Collapsible/Collapsible";
import DNABox from "../../layout/DNABox/DNABox";
import DNAButton from "../DNAButton/DNAButton";
import DNACard from "../../layout/DNACard/DNACard";
import DNAText from "../../type/DNAText/DNAText";
import Iffy from "../../util/Iffy/Iffy";
import colors from "../../../theming/themes/alucio/colors";
import StateProvider, { useInformationMessageState } from './InformationMessageStateProvider';
const styles = StyleSheet.create({
    wrapper: {
        paddingHorizontal: 16,
        borderTopWidth: 1,
    },
    DNACard: {
        minHeight: 'fit-content',
        borderRadius: 4,
    },
    title: {
        marginRight: 16,
        width: '100%',
        fontWeight: 'normal',
        lineHeight: 20,
        whiteSpace: 'inherit',
    },
    header: {
        paddingHorizontal: 16,
        paddingVertical: 10,
    },
    content: {
        backgroundColor: 'white',
        borderBottomLeftRadius: 4,
        borderBottomRightRadius: 4,
    },
    messageItemContainer: {
        paddingVertical: 16,
    },
    rightElementButton: {
        marginLeft: 16,
    },
    secondaryRightElementButton: {
        marginRight: 8,
    },
});
const titleVariance = {
    primary: { color: colors['color-primary-500'] },
    success: { color: colors['color-success-500'] },
    warning: { color: colors['color-warning-500'] },
    danger: { color: colors['color-danger-500'] },
};
const containerVariance = {
    primary: {
        backgroundColor: colors['color-brand2-5'],
        borderColor: colors['color-primary-500'],
    },
    danger: {
        backgroundColor: colors['color-danger-5'],
        borderColor: colors['color-danger-500'],
    },
    success: {
        backgroundColor: colors['color-success-5'],
        borderColor: colors['color-success-500'],
    },
    warning: {
        backgroundColor: colors['color-warning-5'],
        borderColor: colors['color-warning-500'],
    },
};
const Item = (props) => {
    const { variance = 'primary' } = useInformationMessageState();
    const { text, numberOfTextLines, actionButtonFunction, actionButtonText, children, } = props;
    return (<DNABox childFill={0} spacing="between" style={styles.messageItemContainer}>
      {typeof text === 'string' ? <DNAText b1 bold numberOfLines={numberOfTextLines ?? undefined} style={[styles.title, titleVariance[variance]]}>
        {text}
      </DNAText> : <DNABox>{text}</DNABox>}
      <Iffy is={actionButtonFunction && actionButtonText}>
        <DNAButton appearance="outline" status={variance} size="sm" padding="xs" style={styles.rightElementButton} onPress={actionButtonFunction} children={actionButtonText}/>
      </Iffy>
      {children || null}
    </DNABox>);
};
const InformationMessage = (props) => {
    const { actionButtonFunction, actionButtonText, secondaryActionButtonFunction, secondayActionButtonText, secondayVariance = 'primary', text, numberOfLines, variance = 'primary', leftIcon, testID, children, customWrapperStyle, } = props;
    const contentContainerStyle = useMemo(() => [
        styles.header,
        customWrapperStyle,
    ], [customWrapperStyle, variance]);
    return (<DNABox fill appearance="col" spacing="md" testID={testID}>
      <DNACard testID="collapsed-header-container" style={containerVariance[variance]} appearance="outline">
        <DNABox childFill={0} spacing="between" alignY="center" style={contentContainerStyle}>
          <DNABox fill>
            {leftIcon}
            {typeof text === 'string' ? <DNAText b1 bold numberOfLines={numberOfLines ?? undefined} style={[styles.title, titleVariance[variance]]}>
              {text}
            </DNAText> : <DNABox>{text}</DNABox>}
          </DNABox>
          <Iffy is={secondaryActionButtonFunction && secondayActionButtonText}>
            <DNAButton appearance="filled" status={secondayVariance} style={styles.secondaryRightElementButton} onPress={secondaryActionButtonFunction} size="sm" children={secondayActionButtonText}/>
          </Iffy>
          <Iffy is={actionButtonFunction && actionButtonText}>
            <DNAButton appearance="ghostLink" status={variance} padding="none" style={styles.rightElementButton} onPress={actionButtonFunction} children={actionButtonText}/>
          </Iffy>
          {children || null}
        </DNABox>
      </DNACard>
    </DNABox>);
};
const Parent = (props) => {
    const { leftIcon, text, numberOfLines, secondaryActionButtonFunction, secondaryActionButtonText, } = props;
    const { variance = 'primary', toggleCollapse, isCollapsed, } = useInformationMessageState();
    return (<Pressable onPress={toggleCollapse}>
      <DNABox childFill={0} alignY="center" style={styles.header}>
        {leftIcon}
        {typeof text === 'string' ? <DNAText b1 bold numberOfLines={numberOfLines ?? undefined} style={[styles.title, titleVariance[variance]]}>
          {text}
        </DNAText> : <DNABox>{text}</DNABox>}
        <Iffy is={secondaryActionButtonText && secondaryActionButtonFunction}>
          <DNAButton status={variance} onPress={secondaryActionButtonFunction} children={secondaryActionButtonText}/>
        </Iffy>
        <DNAButton appearance="ghostLink" status={variance} onPress={toggleCollapse} children={isCollapsed ? 'View details' : 'Collapse'}/>
      </DNABox>
    </Pressable>);
};
const Items = (props) => {
    const { children } = props;
    const { collapseOverride, isCollapsed, customWrapperStyle, variance = 'primary', } = useInformationMessageState();
    const contentContainerStyle = useMemo(() => [
        styles.wrapper,
        customWrapperStyle,
        { borderTopColor: titleVariance[variance].color },
    ], [customWrapperStyle, variance]);
    return (<Collapsible collapsed={collapseOverride ?? isCollapsed} style={styles.content}>
      {/* CONTENT */}
      <DNABox appearance="col" style={contentContainerStyle}>
        {children}
      </DNABox>
    </Collapsible>);
};
const CollapsibleInfo = (props) => {
    const { variance = 'primary', testID, children, } = props;
    return (<StateProvider {...props}>
      <DNABox fill appearance="col" spacing="md" testID={testID}>
        <DNACard testID="collapsed-header-container" style={containerVariance[variance]} appearance="outline">
          {children}
        </DNACard>
      </DNABox>
    </StateProvider>);
};
InformationMessage.displayName = 'InformationMessage';
InformationMessage.Collapsible = CollapsibleInfo;
CollapsibleInfo.Parent = Parent;
CollapsibleInfo.Items = Items;
CollapsibleInfo.Item = Item;
InformationMessage.Collapsible.Parent = Parent;
InformationMessage.Collapsible.Items = Items;
InformationMessage.Collapsible.Item = Item;
export default InformationMessage;
//# sourceMappingURL=InformationMessage.js.map